<template>
  <div class="auth-page">
    <header class="auth-header">
      <router-link class="auth-brand" :to="homePath">
        <img src="/brain-icon-96.jpg" alt="" width="28" height="28" />
        <span>Remote EMDR</span>
      </router-link>
    </header>

    <div class="auth-card">
      <h1>{{ mode === 'login' ? $t('auth.loginTitle') : $t('auth.registerTitle') }}</h1>
      <p class="auth-sub">{{ $t('auth.subtitle') }}</p>

      <form @submit.prevent="submit">
        <label class="auth-label" for="auth-email">{{ $t('auth.email') }}</label>
        <input
          id="auth-email"
          v-model="email"
          type="email"
          required
          autocomplete="email"
          class="auth-input" />

        <label class="auth-label" for="auth-password">{{ $t('auth.password') }}</label>
        <div class="pw-wrap">
          <input
            id="auth-password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            required
            minlength="8"
            :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
            class="auth-input pw-input" />
          <button type="button" class="pw-toggle" @click="showPassword = !showPassword">
            {{ showPassword ? $t('auth.hidePw') : $t('auth.showPw') }}
          </button>
        </div>
        <p v-if="mode === 'register'" class="auth-hint">{{ $t('auth.passwordHint') }}</p>

        <p v-if="errorMessage" class="auth-error">{{ errorMessage }}</p>
        <p v-if="infoMessage" class="auth-info">{{ infoMessage }}</p>
        <p v-if="devActivationLink" class="auth-info">
          <a :href="devActivationLink">{{ $t('auth.devLink') }}</a>
        </p>
        <p v-if="showResend" class="auth-info">
          <a href="#" @click.prevent="resend">{{ $t('auth.resend') }}</a>
        </p>

        <div v-if="captchaSiteKey" ref="turnstileBox" class="turnstile-box"></div>

        <button class="auth-submit" type="submit" :disabled="busy">
          {{ mode === 'login' ? $t('auth.loginCta') : $t('auth.registerCta') }}
        </button>
      </form>

      <p v-if="mode === 'register'" class="auth-privacy">
        {{ $t('auth.privacyNote') }}
        <router-link :to="privacyPath">{{ $t('consent.privacy') }}</router-link>
      </p>

      <p class="auth-switch">
        {{ mode === 'login' ? $t('auth.noAccount') : $t('auth.haveAccount') }}
        <a href="#" @click.prevent="toggleMode">
          {{ mode === 'login' ? $t('auth.registerLink') : $t('auth.loginLink') }}
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import { login, register, resendActivation, isLoggedIn, getAuthConfig } from './auth';
import { applySeo } from './i18n/head';

export default {
  data() {
    return {
      mode: 'login',
      email: '',
      password: '',
      showPassword: false,
      errorMessage: '',
      infoMessage: '',
      devActivationLink: '',
      showResend: false,
      busy: false,
      captchaSiteKey: '',
      captchaToken: '',
      captchaWidgetId: null
    };
  },
  computed: {
    homePath() {
      return this.$localePath('/');
    },
    dashboardPath() {
      return this.$localePath('/dashboard');
    },
    privacyPath() {
      return this.$localePath('/privacy');
    }
  },
  created() {
    if (isLoggedIn()) {
      this.$router.replace(this.dashboardPath);
      return;
    }
    applySeo({
      locale: this.$locale,
      routeBase: 'login',
      title: this.$t('auth.metaTitle'),
      description: this.$t('auth.metaDescription'),
      noindex: true
    });
    // "Create free account" links elsewhere open straight in register mode
    if (this.$route.query.mode === 'register') {
      this.mode = 'register';
    }
    // Landing back from the emailed activation link
    if (this.$route.query.activated === '1') {
      this.infoMessage = this.$t('auth.activated');
    } else if (this.$route.query.activated === '0') {
      this.errorMessage = this.$t('auth.activationInvalid');
    }
  },
  mounted() {
    this.loadCaptcha();
  },
  methods: {
    async loadCaptcha() {
      let cfg;
      try {
        cfg = await getAuthConfig();
      } catch (e) {
        return; // config endpoint unreachable - fail open (backend still gates)
      }
      this.captchaSiteKey = cfg.captchaSiteKey || '';
      if (!this.captchaSiteKey) return;
      await this.$nextTick(); // ensure the box element exists before rendering
      this.injectTurnstile();
    },
    injectTurnstile() {
      // Render only once the Turnstile API signals it is ready. We use the
      // script's ?onload= callback (not the DOM load event) because render()
      // called before the API is ready silently no-ops.
      if (window.turnstile && window.__emdrTurnstileReady) {
        this.renderCaptcha();
        return;
      }
      window.__emdrTurnstileOnload = () => {
        window.__emdrTurnstileReady = true;
        this.renderCaptcha();
      };
      if (document.getElementById('cf-turnstile-script')) return;
      const s = document.createElement('script');
      s.id = 'cf-turnstile-script';
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
        + '?render=explicit&onload=__emdrTurnstileOnload';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    },
    renderCaptcha() {
      if (!window.turnstile || !this.$refs.turnstileBox || this.captchaWidgetId !== null) return;
      this.captchaWidgetId = window.turnstile.render(this.$refs.turnstileBox, {
        sitekey: this.captchaSiteKey,
        callback: (token) => { this.captchaToken = token; },
        'expired-callback': () => { this.captchaToken = ''; },
        'error-callback': () => { this.captchaToken = ''; }
      });
    },
    resetCaptcha() {
      this.captchaToken = '';
      try {
        if (window.turnstile && this.captchaWidgetId !== null) {
          window.turnstile.reset(this.captchaWidgetId);
        }
      } catch (e) { /* widget gone - ignore */ }
    },
    toggleMode() {
      this.mode = this.mode === 'login' ? 'register' : 'login';
      this.errorMessage = '';
      this.infoMessage = '';
      this.devActivationLink = '';
      this.showResend = false;
    },
    async submit() {
      if (this.captchaSiteKey && !this.captchaToken) {
        this.errorMessage = this.$t('auth.captchaRequired');
        return;
      }
      this.busy = true;
      this.errorMessage = '';
      this.infoMessage = '';
      this.devActivationLink = '';
      this.showResend = false;
      try {
        if (this.mode === 'login') {
          await login(this.email, this.password, this.captchaToken);
          this.$router.push(this.dashboardPath);
        } else {
          const data = await register(this.email, this.password, this.$locale, this.captchaToken);
          this.mode = 'login';
          this.infoMessage = `${this.$t('auth.activationSent')} ${data.email}. ${this.$t('auth.checkInbox')}`;
          if (data.activation_link) {
            this.devActivationLink = data.activation_link;
          }
          this.resetCaptcha();
        }
      } catch (error) {
        const response = error.response && error.response.data;
        if (response && response.code === 'not_activated') {
          this.errorMessage = this.$t('auth.notActivated');
          this.showResend = true;
        } else {
          this.errorMessage = (response && response.message) || this.$t('auth.genericError');
        }
        this.resetCaptcha(); // token is single-use; refresh for the retry
      } finally {
        this.busy = false;
      }
    },
    async resend() {
      this.busy = true;
      this.errorMessage = '';
      this.devActivationLink = '';
      try {
        const data = await resendActivation(this.email, this.password, this.$locale);
        this.showResend = false;
        this.infoMessage = this.$t('auth.resendDone');
        if (data.activation_link) {
          this.devActivationLink = data.activation_link;
        }
      } catch (error) {
        const serverMessage = error.response && error.response.data && error.response.data.message;
        this.errorMessage = serverMessage || this.$t('auth.genericError');
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  background: linear-gradient(150deg, #142b27 0%, #1F5B52 55%, #2B6960 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.auth-header {
  width: 100%;
  padding: 1.1rem clamp(1.5rem, 5vw, 4rem);
}
.auth-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: white;
  font-family: var(--ff-heading);
  font-weight: 600;
  text-decoration: none;
}
.auth-brand img {
  border-radius: 8px;
  object-fit: cover;
}
.auth-card {
  background: var(--c-bg);
  border-radius: 14px;
  padding: 2.25rem 2rem;
  width: min(420px, calc(100vw - 2.5rem));
  margin: clamp(1.5rem, 8vh, 4rem) 1rem 3rem;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
}
.auth-card h1 {
  font-size: 1.5rem;
  margin-bottom: 0.35rem;
  color: var(--c-text);
}
.auth-sub {
  color: var(--c-text-light);
  font-size: 0.92rem;
  margin-bottom: 1.5rem;
}
.auth-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--c-text);
  margin: 0.9rem 0 0.3rem;
}
.auth-input {
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  font-family: var(--ff-body);
  font-size: 0.95rem;
  background: white;
}
.auth-input:focus {
  outline: 2px solid var(--c-primary-light);
  border-color: transparent;
}
.auth-hint {
  font-size: 0.78rem;
  color: var(--c-text-light);
  margin-top: 0.3rem;
}
.pw-wrap {
  display: flex;
  gap: 0.5rem;
}
.pw-input {
  flex: 1;
  min-width: 0;
}
.pw-toggle {
  background: transparent;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  padding: 0 0.9rem;
  font-family: var(--ff-body);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--c-text-light);
  cursor: pointer;
  white-space: nowrap;
}
.pw-toggle:hover {
  color: var(--c-text);
  border-color: var(--c-text-light);
}
.auth-error {
  color: #b3403a;
  font-size: 0.87rem;
  margin-top: 0.9rem;
}
.auth-info {
  color: var(--c-primary);
  font-size: 0.87rem;
  margin-top: 0.9rem;
}
.auth-info a {
  color: var(--c-primary);
  font-weight: 600;
  text-decoration: underline;
}
.turnstile-box {
  margin: 1.1rem 0 0;
  min-height: 65px;
}
.auth-submit {
  width: 100%;
  margin-top: 1.4rem;
  background: var(--c-accent);
  color: #1a2f2c;
  border: none;
  border-radius: 10px;
  padding: 0.85rem;
  font-family: var(--ff-body);
  font-size: 0.98rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--tr-fast);
}
.auth-submit:hover {
  background: var(--c-accent-light);
}
.auth-submit:disabled {
  opacity: 0.6;
  cursor: wait;
}
.auth-privacy {
  margin-top: 1rem;
  font-size: 0.82rem;
  color: var(--c-text-light);
  text-align: center;
}
.auth-privacy a {
  color: var(--c-primary);
  font-weight: 600;
}
.auth-switch {
  margin-top: 1.4rem;
  font-size: 0.88rem;
  color: var(--c-text-light);
  text-align: center;
}
.auth-switch a {
  color: var(--c-primary);
  font-weight: 600;
}
</style>
